import type { CheckBoxTreeModelType } from '@c4-links/vue3-links-components';
import type { FilterDefinitionValue } from '@carrefour-gcs/bff-sdk';
import { isFilterTypeGuard } from '@carrefour-gcs/shared';
import type { CheckStateType, NodeReaderType, TreeNodeShapeType } from '../types/treeNode.type';
import type { TreePathSegmentType, TreeSelectionType } from '../types/treeSelection.type';

/**
 * Reads a node of the API tree.
 */
export function readOption(option: FilterDefinitionValue): TreeNodeShapeType<FilterDefinitionValue> {
  return { key: option.value, children: option.children ?? [] };
}

/**
 * Reads a node of the design-system tree.
 */
export function readTreeNode(node: CheckBoxTreeModelType): TreeNodeShapeType<CheckBoxTreeModelType> {
  return { key: keyOfNode(node), children: node.children ?? [] };
}

/**
 * A node missing from the base tree, or stamped with no level type by the API, is not selectable on its own.
 */
export function locateInBase(
  node: CheckBoxTreeModelType,
  baseNodes: FilterDefinitionValue[] | undefined,
  parentPath: TreePathSegmentType[]
): { baseNode: FilterDefinitionValue; path: TreePathSegmentType[] } | undefined {
  const key = keyOfNode(node);
  const baseNode = baseNodes?.find((option) => option.value === key);
  const type = baseNode?.type;

  if (!baseNode || !type || !isFilterTypeGuard(type)) {
    return undefined;
  }

  return { baseNode, path: [...parentPath, { type, key }] };
}

export function findByKeys(
  nodes: FilterDefinitionValue[],
  path: TreePathSegmentType[]
): FilterDefinitionValue | undefined {
  let level: FilterDefinitionValue[] | undefined = nodes;
  let node: FilterDefinitionValue | undefined;

  for (const segment of path) {
    node = level?.find((option) => option.value === segment.key);

    if (!node) {
      return undefined;
    }

    level = node.children;
  }

  return node;
}

/**
 * URL-encoded key path, so a key repeated under another branch cannot collide.
 */
export function nodeIdOfKeys(keys: string[]): string {
  return keys.map(encodeURIComponent).join('/');
}

export function nodeIdOfSelection(selection: TreeSelectionType): string {
  return nodeIdOfKeys(selection.path.map((segment) => segment.key));
}

function keyOfNode(node: CheckBoxTreeModelType): string {
  const lastEncodedKey = (node.id ?? '').split('/').at(-1) ?? '';

  return decodeURIComponent(lastEncodedKey);
}

export function countLeaves(option: FilterDefinitionValue): number {
  return option.children?.length ? option.children.reduce((count, child) => count + countLeaves(child), 0) : 1;
}

export function collectNodeIds(nodes: CheckBoxTreeModelType[]): Set<string> {
  return nodes.reduce((ids, node) => {
    if (node.id) {
      ids.add(node.id);
    }
    collectNodeIds(node.children ?? []).forEach((id) => ids.add(id));

    return ids;
  }, new Set<string>());
}

export function resolveCheckState(isChecked: boolean, children: CheckBoxTreeModelType[] | undefined): CheckStateType {
  if (!children?.length) {
    return { checked: isChecked, indeterminate: false };
  }

  const allChildrenChecked = children.every((child) => child.checked);
  const anyChildChecked = children.some((child) => child.checked || child.indeterminate);

  return { checked: allChildrenChecked, indeterminate: !allChildrenChecked && anyChildChecked };
}

/**
 * A node whose base subtree is not fully displayed must not be promoted: it would select its hidden descendants.
 */
export function isFullyDisplayed<Node>(
  node: Node,
  baseNode: FilterDefinitionValue | undefined,
  read: NodeReaderType<Node>
): boolean {
  if (!baseNode) {
    return false;
  }

  const baseChildren = baseNode.children ?? [];
  const displayedChildren = read(node).children;

  return (
    displayedChildren.length >= baseChildren.length &&
    displayedChildren.every((child) =>
      isFullyDisplayed(
        child,
        baseChildren.find((baseChild) => baseChild.value === read(child).key),
        read
      )
    )
  );
}
